import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const NeoCreditPurchasePage = () => {
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Check for payment result from backend redirect
    // Backend redirects with ?payment=success|error|cancelled&credits=X&balance=Y
    let paymentStatus = null;
    let credits = null;
    let message = null;
    
    // Check URL search params (BrowserRouter)
    const searchParams = new URLSearchParams(window.location.search);
    paymentStatus = searchParams.get('payment');
    credits = searchParams.get('credits');
    message = searchParams.get('message');
    
    // Fallback: check hash for backward compatibility
    if (!paymentStatus && window.location.hash.includes('?')) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        paymentStatus = hashParams.get('payment');
        credits = hashParams.get('credits');
        message = hashParams.get('message');
      }
    }
    
    if (paymentStatus === 'success') {
      setPaymentModal({
        type: 'success',
        title: 'Payment Successful!',
        message: `${credits || ''} credits have been added to your account.`
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'error') {
      setPaymentModal({
        type: 'error',
        title: 'Payment Failed',
        message: `${message || 'An error occurred during payment'}. Please contact support if you were charged.`
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setPaymentModal({
        type: 'cancelled',
        title: 'Payment Cancelled',
        message: 'Your payment was cancelled. No charges were made.'
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [packagesRes, balanceRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/neo/credits/packages`, { headers }),
        axios.get(`${API}/neo/credits/balance`, { headers }),
        axios.get(`${API}/neo/credits/transactions?limit=20`, { headers })
      ]);
      
      setPackages(packagesRes.data.packages || []);
      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
      setError(error.response?.data?.detail || 'Failed to load credit information');
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    try {
      setPurchasing(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/neo/credits/purchase`, 
        { package_id: packageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      alert('Failed to initiate purchase. Please try again.');
      setPurchasing(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Payment modal needs to render even during loading/error states
  const PaymentModal = () => paymentModal && (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl border border-slate-700 max-w-md w-full p-6 shadow-2xl">
        <div className="text-center">
          {paymentModal.type === 'success' && (
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
          {paymentModal.type === 'error' && (
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
          {paymentModal.type === 'cancelled' && (
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          )}
          <h3 className={`text-xl font-bold mb-2 ${
            paymentModal.type === 'success' ? 'text-green-400' :
            paymentModal.type === 'error' ? 'text-red-400' : 'text-yellow-400'
          }`}>
            {paymentModal.title}
          </h3>
          <p className="text-gray-300 mb-6">{paymentModal.message}</p>
          <button
            onClick={() => {
              setPaymentModal(null);
              if (paymentModal.type === 'success') {
                fetchData();
              }
            }}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              paymentModal.type === 'success' 
                ? 'bg-green-600 hover:bg-green-500 text-white'
                : paymentModal.type === 'error'
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-yellow-600 hover:bg-yellow-500 text-white'
            }`}
          >
            {paymentModal.type === 'success' ? 'Great!' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <PaymentModal />
        <div className="flex items-center justify-center p-8">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          <span className="ml-3 text-gray-400 text-lg">Loading credit information...</span>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PaymentModal />
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Neo Credits</h1>
            <p className="text-purple-200">Purchase credits for Neo AI assistant</p>
          </div>
          <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Credit Information</h3>
            <p className="text-red-300">{error}</p>
            <button
              onClick={fetchData}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PaymentModal />
      <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Neo Credits</h1>
        <p className="text-purple-200">Purchase credits for Neo AI assistant</p>
      </div>

      {/* Current Balance Card */}
      {balance && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Your Current Balance</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-bold text-purple-400">{balance.credits_remaining?.toLocaleString() || 0}</span>
                <span className="text-gray-400 text-lg">credits remaining</span>
              </div>
              <div className="mt-3 text-sm text-gray-400 space-x-4">
                <span>Used: <strong className="text-white">{balance.credits_used?.toLocaleString() || 0}</strong></span>
                <span>•</span>
                <span>Total Available: <strong className="text-white">{balance.total_available?.toLocaleString() || 0}</strong></span>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center border-4 border-purple-400/30">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate Limit Information Card */}
      <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-purple-300 mb-2">⚡ Neo AI Usage Limits</h3>
            <div className="space-y-2 text-sm text-purple-200">
              <p><strong className="text-white">Rate Limit:</strong> 10 requests per minute</p>
              <p><strong className="text-white">Credit Cost:</strong> 1-10 credits per query (based on complexity)</p>
              <p className="text-purple-300 mt-3">
                When you use Neo via mobile app, you'll see a countdown timer if you approach the rate limit. 
                This ensures fair usage and system stability for all users.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Packages */}
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">Available Credit Packages</h2>
          <p className="text-sm text-gray-400 mt-1">Choose a package and complete payment securely via PayPal</p>
        </div>
        
        <div className="p-6">
          {packages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {packages.map((pkg) => (
                <div key={pkg.id} className={`relative bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-all border-2 ${
                  pkg.popular ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-slate-600'
                }`}>
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                        POPULAR
                      </span>
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-white mb-1 text-center">{pkg.label}</h3>
                    <div className="text-center mb-3">
                      <div className="text-3xl font-bold text-purple-400">
                        {pkg.credits.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-400">credits</div>
                    </div>
                    <div className="text-center mb-4 pb-4 border-b border-slate-600">
                      <div className="text-2xl font-bold text-white">£{pkg.price_gbp.toFixed(2)}</div>
                      <div className="text-xs text-gray-400">(inc. VAT)</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {(pkg.price_gbp / pkg.credits * 100).toFixed(2)}p per credit
                      </div>
                    </div>
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={purchasing}
                      className={`w-full py-2.5 rounded-lg font-semibold transition-all ${
                        pkg.popular
                          ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-sm'
                          : 'bg-slate-600 hover:bg-slate-500 text-white border border-slate-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {purchasing ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p>No credit packages available. Please contact support.</p>
            </div>
          )}
        </div>
      </div>

      {/* Transaction History - Collapsible */}
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <button
          onClick={() => setHistoryExpanded(!historyExpanded)}
          className="w-full px-6 py-4 border-b border-slate-700 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
          data-testid="purchase-history-toggle"
        >
          <div className="text-left">
            <h2 className="text-xl font-semibold text-white">Purchase History</h2>
            <p className="text-sm text-gray-400 mt-1">Your credit purchase transactions</p>
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${historyExpanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {historyExpanded && (
          <div className="p-6">
            {transactions.filter(t => t.type === 'purchase' && t.status === 'completed').length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase" style={{ width: '70%' }}>Transaction Details</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase" style={{ width: '25%' }}>Document</th>
                      <th style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {transactions.filter(t => t.type === 'purchase' && t.status === 'completed').map((txn) => (
                      <tr key={txn.transaction_id} className="hover:bg-slate-700/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span className="text-sm text-gray-400 whitespace-nowrap">
                              {formatTimestamp(txn.timestamp)}
                            </span>
                            <span className="text-sm text-white">
                              {txn.package_label || 'Credit Package'}
                            </span>
                            <span className="text-sm font-semibold text-purple-400">
                              +{txn.amount?.toLocaleString()} credits
                            </span>
                            <span className="text-sm font-semibold text-white">
                              £{txn.price_gbp?.toFixed(2)}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              txn.status === 'completed' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {txn.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {(txn.paypal_capture_id || txn.paypal_order_id) ? (
                            <button
                              onClick={async () => {
                                if (downloadingInvoice === txn.transaction_id) return;
                                setDownloadingInvoice(txn.transaction_id);
                                try {
                                  const response = await fetch(`${API}/neo/credits/invoice/${txn.transaction_id}`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                  });
                                  if (!response.ok) {
                                    const error = await response.json();
                                    throw new Error(error.detail || 'Failed to download');
                                  }
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `INV-NeoCredits-${txn.transaction_id.slice(-8)}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  a.remove();
                                } catch (error) {
                                  setError(error.message || 'Failed to download invoice');
                                } finally {
                                  setDownloadingInvoice(null);
                                }
                              }}
                              disabled={downloadingInvoice === txn.transaction_id}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium rounded-lg transition-all duration-200 ${
                                downloadingInvoice === txn.transaction_id
                                  ? 'opacity-75 cursor-wait'
                                  : 'hover:scale-105 active:scale-95'
                              }`}
                              title="Download Invoice"
                            >
                              {downloadingInvoice === txn.transaction_id ? (
                                <>
                                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Invoice
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No purchase history yet</p>
              </div>
            )}}
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default NeoCreditPurchasePage;
